/**
 * UranIA PA — Admin Tipi Documento — lista + create + edit inline (admin).
 *
 * Pagina che permette al platform_admin / tenant_admin di:
 *   - Vedere tutti i modelli di documento (core + tenant)
 *   - Crearne di nuovi (scope core o tenant)
 *   - Modificarli (sezioni + label + icona)
 *
 * Sostituisce la card "ROADMAP" precedente su /admin: ora la pagina e'
 * operativa e il funzionario nel ribbon Mod5 troverà i modelli caricati qui.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { TipoDocumentoForm } from '../../../components/admin/TipoDocumentoForm.tsx';
import {
  listTipiAdmin,
  TipiDocumentoApiError,
} from '../../../lib/admin/tipi-documento-api.ts';
import type {
  ListTipiDocumentoDto,
  TipoDocumentoDto,
} from '../../../lib/admin/tipi-documento-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../../lib/auth/rbac.ts';
import { Route as AmministrazioneSegmentRoute } from '../../_amministrazione.tsx';

type EditingState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'editing'; tipo: TipoDocumentoDto };

function StatusBadge(props: { readonly tipo: TipoDocumentoDto }): JSX.Element {
  if (props.tipo.is_core) {
    return (
      <span
        className="badge"
        style={{ backgroundColor: '#0d6efd', color: '#fff', fontSize: '0.65rem' }}
      >
        CORE
      </span>
    );
  }
  if (props.tipo.tenant_id !== null) {
    return (
      <span
        className="badge"
        style={{
          backgroundColor: '#e7f1ff',
          color: '#084298',
          border: '1px solid #b6d4fe',
          fontSize: '0.65rem',
        }}
      >
        TENANT
      </span>
    );
  }
  return (
    <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
      CUSTOM
    </span>
  );
}

export function AdminTipiDocumentoPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);

  const [editing, setEditing] = useState<EditingState>({ kind: 'idle' });
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'core' | 'tenant' | 'custom'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const query = useQuery<ListTipiDocumentoDto, TipiDocumentoApiError>({
    queryKey: ['admin', 'tipi-documento'],
    queryFn: listTipiAdmin,
    enabled: isAuthenticated && isAdmin,
    staleTime: 15_000,
  });

  const filtered = useMemo(() => {
    const items = query.data?.items ?? [];
    const q = search.trim().toLowerCase();
    return items.filter((tp) => {
      if (scopeFilter === 'core' && !tp.is_core) return false;
      if (scopeFilter === 'tenant' && (tp.is_core || tp.tenant_id === null)) return false;
      if (scopeFilter === 'custom' && tp.is_core) return false;
      if (activeFilter === 'active' && !tp.is_active) return false;
      if (activeFilter === 'inactive' && tp.is_active) return false;
      if (q.length === 0) return true;
      return (
        tp.tipo_id.toLowerCase().includes(q) ||
        tp.label.toLowerCase().includes(q) ||
        tp.descrizione.toLowerCase().includes(q) ||
        tp.titolo_docx.toLowerCase().includes(q)
      );
    });
  }, [query.data?.items, search, scopeFilter, activeFilter]);

  const hasFiltersActive =
    search.length > 0 || scopeFilter !== 'all' || activeFilter !== 'all';

  const resetFilters = (): void => {
    setSearch('');
    setScopeFilter('all');
    setActiveFilter('all');
  };

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-tipi-doc-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.tipi_documento.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isAdmin) {
    return (
      <section data-testid="admin-tipi-doc-forbidden">
        <h1 className="h3 mb-3">{t('admin.tipi_documento.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-tipi-doc" data-testid="admin-tipi-doc-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>

      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div
          className="d-flex justify-content-between align-items-start gap-3 flex-wrap position-relative"
          style={{ zIndex: 1 }}
        >
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            >
              ADMIN · MODELLI DOCUMENTO
            </span>
            <h1 className="h3 mb-2">{t('admin.tipi_documento.title')}</h1>
            <p className="mb-0 opacity-90 small">{t('admin.tipi_documento.subtitle')}</p>
          </div>
          <button
            type="button"
            className="btn btn-light"
            onClick={(): void => setEditing({ kind: 'creating' })}
            data-testid="admin-tipi-doc-new"
          >
            + {t('admin.tipi_documento.actions.new')}
          </button>
        </div>
      </div>

      {query.isLoading && (
        <p role="status" aria-live="polite">
          {t('admin.tipi_documento.loading')}
        </p>
      )}
      {query.isError && (
        <div className="alert alert-danger" role="alert">
          <strong>{t('admin.tipi_documento.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {/* Editor sopra la lista (se attivo) */}
      {editing.kind !== 'idle' && (
        <div className="card shadow-sm border-0 mb-4" data-testid="admin-tipi-doc-editor">
          <div className="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {editing.kind === 'creating'
                ? t('admin.tipi_documento.form.heading_create')
                : t('admin.tipi_documento.form.heading_edit', {
                    label: editing.tipo.label,
                  })}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={(): void => setEditing({ kind: 'idle' })}
            >
              {t('admin.tipi_documento.actions.close_editor')}
            </button>
          </div>
          <div className="card-body">
            <TipoDocumentoForm
              mode={editing.kind === 'creating' ? 'create' : 'edit'}
              {...(editing.kind === 'editing' ? { initial: editing.tipo } : {})}
              onSuccess={(): void => {
                setTimeout(() => setEditing({ kind: 'idle' }), 1500);
              }}
            />
          </div>
        </div>
      )}

      {/* Pannello filtri */}
      {query.isSuccess && (
        <div className="card shadow-sm border-0 mb-3" data-testid="admin-tipi-doc-filters">
          <div className="card-body py-2 px-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label small text-muted mb-1">
                  {t('admin.tipi_documento.filters.search_label')}
                </label>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  value={search}
                  onChange={(e): void => setSearch(e.target.value)}
                  placeholder={t('admin.tipi_documento.filters.search_placeholder')}
                  data-testid="admin-tipi-doc-filter-search"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">
                  {t('admin.tipi_documento.filters.scope_label')}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={scopeFilter}
                  onChange={(e): void => setScopeFilter(e.target.value as 'all' | 'core' | 'tenant' | 'custom')}
                  data-testid="admin-tipi-doc-filter-scope"
                >
                  <option value="all">{t('admin.tipi_documento.filters.scope_all')}</option>
                  <option value="core">{t('admin.tipi_documento.filters.scope_core')}</option>
                  <option value="tenant">{t('admin.tipi_documento.filters.scope_tenant')}</option>
                  <option value="custom">{t('admin.tipi_documento.filters.scope_custom')}</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">
                  {t('admin.tipi_documento.filters.active_label')}
                </label>
                <select
                  className="form-select form-select-sm"
                  value={activeFilter}
                  onChange={(e): void => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  data-testid="admin-tipi-doc-filter-active"
                >
                  <option value="all">{t('admin.tipi_documento.filters.active_all')}</option>
                  <option value="active">{t('admin.tipi_documento.status.active')}</option>
                  <option value="inactive">{t('admin.tipi_documento.status.inactive')}</option>
                </select>
              </div>
              <div className="col-md-1 d-flex justify-content-end">
                {hasFiltersActive && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={resetFilters}
                    title={t('admin.tipi_documento.filters.reset')}
                    data-testid="admin-tipi-doc-filter-reset"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {hasFiltersActive && (
              <p className="small text-muted mb-0 mt-2">
                {t('admin.tipi_documento.filters.results_count', {
                  shown: filtered.length,
                  total: query.data.total,
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Tabella tipi */}
      {query.isSuccess && (
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom py-2 px-3">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {t('admin.tipi_documento.list_heading', { count: query.data.total })}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" data-testid="admin-tipi-doc-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.tipi_documento.columns.label')}</th>
                  <th scope="col">{t('admin.tipi_documento.columns.tipo_id')}</th>
                  <th scope="col">{t('admin.tipi_documento.columns.scope')}</th>
                  <th scope="col">{t('admin.tipi_documento.columns.sezioni')}</th>
                  <th scope="col">{t('admin.tipi_documento.columns.version')}</th>
                  <th scope="col">{t('admin.tipi_documento.columns.status')}</th>
                  <th scope="col" className="text-end">
                    <span className="visually-hidden">
                      {t('admin.tipi_documento.columns.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted small fst-italic py-3">
                      {t('admin.tipi_documento.filters.empty')}
                    </td>
                  </tr>
                )}
                {filtered.map((tipo) => (
                  <tr
                    key={`${tipo.tipo_id}|${tipo.tenant_id ?? 'core'}`}
                    data-testid={`admin-tipi-doc-row-${tipo.tipo_id}`}
                    style={{ opacity: tipo.is_active ? 1 : 0.6 }}
                  >
                    <td>
                      <strong>{tipo.label}</strong>
                      <br />
                      <small className="text-muted">{tipo.descrizione || '—'}</small>
                    </td>
                    <td>
                      <code className="small">{tipo.tipo_id}</code>
                    </td>
                    <td>
                      <StatusBadge tipo={tipo} />
                    </td>
                    <td>{tipo.sezioni.length}</td>
                    <td>
                      <code className="small">v{tipo.version}</code>
                    </td>
                    <td>
                      {tipo.is_active ? (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#d1e7dd',
                            color: '#0f5132',
                            fontSize: '0.65rem',
                          }}
                        >
                          {t('admin.tipi_documento.status.active')}
                        </span>
                      ) : (
                        <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                          {t('admin.tipi_documento.status.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(): void => setEditing({ kind: 'editing', tipo })}
                        data-testid={`admin-tipi-doc-edit-${tipo.tipo_id}`}
                      >
                        {t('admin.tipi_documento.actions.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AmministrazioneSegmentRoute,
  path: '/admin/tipi-documento',
  component: AdminTipiDocumentoPage,
});
